'use client';

import { useState, useEffect, useRef, FC, useCallback } from 'react';
import { InputElementBase } from '../utils/types';
import {
  FormControl,
  FormItem,
  FormMessage,
  FormLabel,
} from '@/components/ui/form';
import Cross from '../icons/Cross';

interface Option {
  value: string;
  text: string;
  selected: boolean;
}

export type SelectOption = {
  value: string;
  label: string;
};

interface DropdownProps {
  id: string;
  selectOptions: SelectOption[];
  initialSelected?: string[];
}

const MultiSelect: FC<
  DropdownProps &
    Pick<
      InputElementBase,
      'label' | 'name' | 'onBlur' | 'onChange' | 'ref' | 'placeholder'
    >
> = ({
  id,
  label,
  selectOptions,
  initialSelected = [],
  name,
  onBlur,
  onChange,
  ref,
  placeholder,
}) => {
  const initializeOptions = useCallback(() => {
    return selectOptions.map((opt) => ({
      value: opt.value,
      text: opt.label,
      selected: initialSelected.includes(opt.value),
    }));
  }, [selectOptions, initialSelected]);

  const [options, setOptions] = useState<Option[]>(initializeOptions);
  const [selected, setSelected] = useState<number[]>(
    options
      .map((opt, idx) => (opt.selected ? idx : -1))
      .filter((idx) => idx !== -1)
  );
  const [show, setShow] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleSelectChange = (values: string[]) => {
    if (onChange) onChange({ target: { name, value: values } });
  };

  const select = (index: number) => {
    const updatedOptions = [...options];
    updatedOptions[index].selected = !updatedOptions[index].selected;

    const updatedSelected = updatedOptions
      .map((opt, idx) => (opt.selected ? idx : -1))
      .filter((idx) => idx !== -1);

    setOptions(updatedOptions);
    setSelected(updatedSelected);

    handleSelectChange(updatedSelected.map((i) => updatedOptions[i].value));
  };

  const remove = (index: number) => {
    select(index);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block w-full">
      <FormItem>
        {label && <FormLabel>{label}</FormLabel>}
        <FormControl>
          <div>
            <select
              multiple
              name={name}
              onBlur={onBlur}
              onChange={onChange || (() => {})}
              value={selected.map((i) => options[i].value)}
              ref={ref}
              className="hidden"
              id={id}
            >
              {selectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <div ref={triggerRef} onClick={() => setShow(!show)}>
              <div className="flex flex-wrap gap-2 rounded border border-stroke p-2 dark:border-form-strokedark dark:bg-form-input cursor-pointer min-h-16 items-center">
                {selected.length === 0 && (
                  <span className="text-gray-400">{placeholder}</span>
                )}
                {selected.map((i) => (
                  <div
                    key={options[i].value}
                    className="my-1.5 flex items-center justify-center rounded border-[.5px] border-stroke bg-gray px-2.5 py-1.5 text-sm font-medium dark:border-strokedark dark:bg-white/30"
                  >
                    {options[i].text}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(i);
                      }}
                      className="cursor-pointer hover:text-red-500 ml-2"
                    >
                      <Cross width="15" height="15" />
                    </span>
                  </div>
                ))}
                <svg
                  className="ml-auto h-7 w-7 text-gray-400 self-center"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            {show && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 mt-1 z-1000 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg dark:bg-form-input dark:border-form-strokedark"
              >
                {options.map((option, index) => (
                  <div
                    key={option.value}
                    className={`cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 ${
                      option.selected ? 'bg-gray-200 dark:bg-gray-700' : ''
                    }`}
                    onClick={() => select(index)}
                  >
                    {option.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </div>
  );
};

export default MultiSelect;
